import { Box, Stack, Typography, Button, Drawer } from "@mui/material";
import React, { useState } from "react";
import { Tooltip } from "@mui/material";
import { eurToKmf, formatKmf } from "./utils/currency"; // 🆕 adapter le chemin si besoin

/* ═══════════════════════════════════════════════════════════
   🆕 PÉRIODES — unité de vente d'un tarif

   Sur un plan weekly ou monthly, le montant saisi est le prix de LA PÉRIODE
   ENTIÈRE, pas d'une nuit. C'est l'erreur la plus facile à commettre dans
   cette grille : un hébergeur qui tape 23 au lieu de 690 vend son mois au
   prix d'une nuit. Chaque libellé de saisie porte donc son unité.

   "long_stay" est l'ancienne valeur, lue comme mensuelle.
   ═══════════════════════════════════════════════════════════ */
const UNIT = {
  short_stay: { label: "nuit",     nights: 1  },
  weekly:     { label: "semaine",  nights: 7  },
  monthly:    { label: "mois",     nights: 30 },
};

const normalizeStayType = (t) =>
  t === "long_stay" ? "monthly" : (["short_stay", "weekly", "monthly"].includes(t) ? t : "short_stay");

const unitOf = (stayType) => UNIT[normalizeStayType(stayType)];
const isPeriodPlan = (stayType) => normalizeStayType(stayType) !== "short_stay";

const InventoryGrid = ({
  room,
  daysOfMonth,
  startIndex,
  daysPerPage,
  selectedDays,
  setSelectedDays,
  editType,
  setEditType,
  editModeRoom,
  setEditModeRoom,
  isSelecting,
  setIsSelecting,
  selectionStart,
  setSelectionStart,
  editRatePlan,
  setEditRatePlan,
  bulkDrawer,
  setBulkDrawer,
  bulkValue,
  setBulkValue,
  handleApplyBulk,
  setActivePage,
  allRatePlans = [] // ✅ contient désormais roomIds
}) => {

   const [selectedRatePlanForPrice, setSelectedRatePlanForPrice] = useState(null);

  const labelStyle = {
    backgroundColor: "#F8FAFC",
    borderRight: "1px solid #E2E8F0",
    borderBottom: "1px solid #E2E8F0",
    // 🆕 MISE EN PAGE — un peu d'air : les libellés de tarif étaient collés
    // aux bords et tronqués dès que le nom dépassait quelques caractères.
    px: 2.5,
    py: 2.25,
    fontWeight: 600,
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const cellStyle = {
    borderLeft: "1px solid #E2E8F0",
    borderBottom: "1px solid #E2E8F0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // 🆕 MISE EN PAGE — 48px ne laissait pas respirer les deux lignes
    // (montant + KMF) ; 56 suffit sans étirer la grille.
    height: 56,
    px: 0.5,
    // 🆕 MISE EN PAGE — sans minWidth 0 + overflow, le montant en KMF
    // (nowrap) impose sa largeur à la colonne : la grille dépassait de la
    // carte et la dernière journée se retrouvait coupée.
    minWidth: 0,
    overflow: "hidden",
    fontSize: 13,
    transition: "all 0.15s ease",
    "&:hover": { backgroundColor: "#EEF2FF" }
  };

  const parseDate = (dateStr) => {
    const [y, m, d] = dateStr.split("-");
    return new Date(y, m - 1, d);
  };

  const roomIdStr = (room.roomId ?? room._id)?.toString();

  // 🆕 MISE EN PAGE — jours réellement affichés.
  //
  // La dernière page d'un mois en contient souvent moins de 14. En gardant
  // repeat(daysPerPage), la grille définissait 14 colonnes pour 2 cellules :
  // les rangées Stock et Statut ne repartaient pas à la ligne et venaient
  // s'aligner à droite des tarifs.
  const visibleDays = daysOfMonth.slice(startIndex, startIndex + daysPerPage);

  // 🆕 ASSOCIATION — les tarifs affichés pour cette chambre viennent de roomIds (pas des prix existants).
  // C'est ce qui fait apparaître une ligne de tarif MÊME sans prix encore saisi → cliquable pour la remplir.
  const ratePlans = allRatePlans
    .filter(plan => (plan.roomIds || []).map(id => id?.toString?.() ?? id).includes(roomIdStr))
    .map(plan => ({ id: plan._id.toString(), name: plan.name, stayType: normalizeStayType(plan.stayType) }));

  // ✅ hasPrice = au moins un vrai prix saisi quelque part dans le mois affiché
  const hasPrice = room.days?.some(day => day.rates?.some(r => r.price > 0));

  // 🆕 PÉRIODES — plan actuellement ciblé par le drawer, pour adapter les libellés
  const drawerPlan = bulkDrawer.ratePlanId
    ? allRatePlans.find(p => p._id.toString() === bulkDrawer.ratePlanId?.toString())
    : null;
  const drawerUnit = drawerPlan ? unitOf(drawerPlan.stayType) : UNIT.short_stay;
  const drawerIsPeriod = drawerPlan ? isPeriodPlan(drawerPlan.stayType) : false;

  return (
    <Box sx={{ border: "1px solid #E2E8F0", borderRadius: 3, overflow: "hidden", mb: 5 }}>

      {/* ROOM HEADER */}
      <Box sx={{
        backgroundColor: "#F8FAFC",
        px: 3, py: 2.5,
        borderBottom: "1px solid #E2E8F0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <Typography fontWeight={600}>{room.title}</Typography>

        <Box sx={{ display: "flex", gap: 1 }}>

        {/* Pas de prix → seul badge utile ici */}
        {!hasPrice && ratePlans.length > 0 && (
          <Box
            onClick={() => { setBulkDrawer({ open: true, type: "price", roomId: room.roomId }); setBulkValue(""); }}
            sx={{
              px: 2, py: 0.5,
              backgroundColor: "#FEF3C7",
              color: "#92400E",
              borderRadius: 2,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              "&:hover": { backgroundColor: "#FDE68A" }
            }}
          >
            ⚡ Aucun prix — Cliquez pour configurer
          </Box>
        )}

      </Box>
      </Box>

      {/* GRID */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: `190px repeat(${visibleDays.length}, minmax(0, 1fr))`,
        userSelect: "none"
      }}>

        {/* ═══ RATE PLAN ROWS ═══ */}
        {ratePlans.length === 0 ? (
          <>
            <Box sx={{ ...labelStyle, color: "#94A3B8" }}>Aucun tarif associé</Box>
            {visibleDays.map((_, index) => (
              <Box key={index} sx={{ ...cellStyle, backgroundColor: "#F8FAFC", color: "#CBD5E1" }}>
                —
              </Box>
            ))}
          </>
        ) : (
          ratePlans.map(plan => {
            const unit = unitOf(plan.stayType);          // 🆕 PÉRIODES
            const period = isPeriodPlan(plan.stayType);  // 🆕 PÉRIODES

            return (
            <React.Fragment key={plan.id}>
              {/* 🆕 Tooltip MUI attend `title`, pas `label` : sans ça l'infobulle
                  ne s'affichait pas du tout sur les noms de tarif tronqués. */}
              <Tooltip title={period ? `${plan.name} — prix pour ${unit.label === "mois" ? "un mois" : "une semaine"} entier${unit.label === "mois" ? "" : "e"}` : plan.name}>
                <Box
                  sx={{ ...labelStyle }}
                  onClick={() => {
                    setBulkDrawer({
                      open: true,
                      type: "price",
                      roomId: room.roomId,
                      ratePlanId: plan.id  // ✅ déjà le bon rate plan
                    });
                    setBulkValue("");
                  }}
                >
                  <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                    {plan.name}
                    {/* 🆕 PÉRIODES — l'unité de vente, toujours visible */}
                    {period && (
                      <Box component="span" sx={{ ml: 1, fontSize: 10, color: "var(--k-green)", fontWeight: 700 }}>
                        · / {unit.label}
                      </Box>
                    )}
                  </Box>
                  {/* ✅ icône bulk */}
                  <Box sx={{ fontSize: 10, color: "#94A3B8", ml: 1, flexShrink: 0 }}>
                    ✎
                  </Box>
                </Box>
              </Tooltip>

              {visibleDays.map((dateStr, index) => {
                const dayData = room.days[startIndex + index];
                const rate = dayData?.rates?.find(r =>
                  (r.ratePlanId?._id || r.ratePlanId)?.toString() === plan.id
                );
                const price = rate?.price ?? null;

                // 🆕 DEVISES — on prend priceKMF si le backend l'envoie, sinon on convertit ici.
                const priceKMF = price === null
                  ? null
                  : (rate?.priceKMF ?? eurToKmf(price));

                const isSelected =
                  selectedDays.tarif?.includes(index) &&
                  editType === "tarif" &&
                  editModeRoom === room.roomId &&
                  editRatePlan === plan.id;

                const isFirst = isSelected && !(selectedDays.tarif?.includes(index - 1));
                const isLast = isSelected && !(selectedDays.tarif?.includes(index + 1));

                // 🆕 PÉRIODES — l'infobulle dit l'unité, et donne l'équivalent
                // par nuit qui n'a qu'une valeur indicative.
                const title = price === null
                  ? (period
                      ? `Cliquez pour définir le prix d'${unit.label === "mois" ? "un mois" : "une semaine"}`
                      : "Cliquez pour définir un prix")
                  : (period
                      ? `${price} € / ${unit.label} · ${formatKmf(priceKMF)} KMF · soit ≈ ${Math.round(price / unit.nights)} €/nuit`
                      : `${price} € · ${formatKmf(priceKMF)} KMF`);

                return (
                  <Box
                    key={index}
                    title={title}
                    onMouseDown={() => {
                      setIsSelecting(true);
                      setSelectionStart(index);
                      setEditModeRoom(room.roomId);
                      setEditType("tarif");
                      setEditRatePlan(plan.id);
                      setSelectedDays({ tarif: [index], inventory: [], status: [] });
                    }}
                    onMouseEnter={() => {
                      if (isSelecting && editType === "tarif" && editModeRoom === room.roomId && editRatePlan === plan.id && selectionStart !== null) {
                        const start = Math.min(selectionStart, index);
                        const end = Math.max(selectionStart, index);
                        setSelectedDays({ tarif: Array.from({ length: end - start + 1 }, (_, i) => start + i), inventory: [], status: [] });
                      }
                    }}
                    onMouseUp={() => { setIsSelecting(false); setSelectionStart(null); }}
                    sx={{
                      ...cellStyle,
                      flexDirection: "column",
                      gap: 0,
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#4F46E5" : price === null ? "#FEF3C7" : "#FFFFFF",
                      color: isSelected ? "white" : price === null ? "#92400E" : "inherit",
                      borderTopLeftRadius: isFirst ? 8 : 0,
                      borderBottomLeftRadius: isFirst ? 8 : 0,
                      borderTopRightRadius: isLast ? 8 : 0,
                      borderBottomRightRadius: isLast ? 8 : 0,
                    }}
                  >
                    {price !== null ? (
                      <>
                        <Box sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2, whiteSpace: "nowrap" }}>
                          {price} €
                        </Box>
                        {/* Le montant en KMF reste sur UNE ligne : sans nowrap,
                            un prix à 6 chiffres passait à la ligne et cassait
                            la hauteur de toute la rangée.
                            L'unité de vente n'est pas répétée ici, elle est
                            portée par le nom du tarif à gauche : la répéter
                            dans chaque case surcharge la grille pour rien. */}
                        <Box sx={{
                          fontSize: 9.5,
                          lineHeight: 1.2,
                          whiteSpace: "nowrap",
                          color: isSelected ? "rgba(255,255,255,0.75)" : "#94A3B8"
                        }}>
                          {formatKmf(priceKMF)} KMF
                        </Box>
                      </>
                    ) : "—"}
                  </Box>
                );
              })}
            </React.Fragment>
            );
          })
        )}

        {/* ═══ STOCK ROW ═══ */}
        <Box
          sx={labelStyle}
          onClick={() => { setBulkDrawer({ open: true, type: "inventory", roomId: room.roomId }); setBulkValue(""); }}
        >
          Stock
        </Box>

        {visibleDays.map((_, index) => {
          const dayData = room.days[startIndex + index];
          const available = dayData?.status === "closed" ? 0 : (dayData?.inventory ?? 0);

          const isSelected =
            selectedDays.inventory?.includes(index) &&
            editType === "inventory" &&
            editModeRoom === room.roomId;

          const isFirst = isSelected && !(selectedDays.inventory?.includes(index - 1));
          const isLast = isSelected && !(selectedDays.inventory?.includes(index + 1));

          return (
            <Box
              key={index}
              onMouseDown={() => {
                setIsSelecting(true);
                setSelectionStart(index);
                setEditModeRoom(room.roomId);
                setEditType("inventory");
                setSelectedDays({ tarif: [], inventory: [index], status: [] });
              }}
              onMouseEnter={() => {
                if (isSelecting && editType === "inventory" && editModeRoom === room.roomId && selectionStart !== null) {
                  const start = Math.min(selectionStart, index);
                  const end = Math.max(selectionStart, index);
                  setSelectedDays({ tarif: [], inventory: Array.from({ length: end - start + 1 }, (_, i) => start + i), status: [] });
                }
              }}
              onMouseUp={() => { setIsSelecting(false); setSelectionStart(null); }}
              sx={{
                ...cellStyle,
                cursor: "pointer",
                backgroundColor: isSelected ? "#4F46E5" : available === 0 ? "#FEE2E2" : "#FFFFFF",
                color: isSelected ? "white" : "inherit",
                borderTopLeftRadius: isFirst ? 8 : 0,
                borderBottomLeftRadius: isFirst ? 8 : 0,
                borderTopRightRadius: isLast ? 8 : 0,
                borderBottomRightRadius: isLast ? 8 : 0,
              }}
            >
              {available}
            </Box>
          );
        })}

        {/* ═══ STATUS ROW ═══ */}
        <Box sx={labelStyle}>Statut</Box>

        {visibleDays.map((_, index) => {
          const dayData = room.days[startIndex + index];
          const status = dayData?.status ?? "closed";
          const isClosed = status === "closed";

          const isSelected =
            selectedDays.status?.includes(index) &&
            editType === "status" &&
            editModeRoom === room.roomId;

          const isFirst = isSelected && !(selectedDays.status?.includes(index - 1));
          const isLast = isSelected && !(selectedDays.status?.includes(index + 1));

          return (
            <Box
              key={index}
              onMouseDown={() => {
                setIsSelecting(true);
                setSelectionStart(index);
                setEditModeRoom(room.roomId);
                setEditType("status");
                setSelectedDays({ tarif: [], inventory: [], status: [index] });
              }}
              onMouseEnter={() => {
                if (isSelecting && editType === "status" && editModeRoom === room.roomId && selectionStart !== null) {
                  const start = Math.min(selectionStart, index);
                  const end = Math.max(selectionStart, index);
                  setSelectedDays({ tarif: [], inventory: [], status: Array.from({ length: end - start + 1 }, (_, i) => start + i) });
                }
              }}
              onMouseUp={() => { setIsSelecting(false); setSelectionStart(null); }}
              sx={{
                ...cellStyle,
                cursor: "pointer",
                fontWeight: 600,
                backgroundColor: isSelected ? "#4F46E5" : isClosed ? "#F1F5F9" : "#DCFCE7",
                color: isSelected ? "white" : isClosed ? "#475569" : "#166534",
                borderTopLeftRadius: isFirst ? 8 : 0,
                borderBottomLeftRadius: isFirst ? 8 : 0,
                borderTopRightRadius: isLast ? 8 : 0,
                borderBottomRightRadius: isLast ? 8 : 0,
              }}
            >
              {isClosed ? "F" : "O"}
            </Box>
          );
        })}
      </Box>

      {/* BULK DRAWER */}
      <Drawer
        anchor="right"
        open={bulkDrawer.open && bulkDrawer.roomId === room.roomId}
        onClose={() => setBulkDrawer({ open: false })}
        PaperProps={{ sx: { width: 380, borderTopLeftRadius: 16, borderBottomLeftRadius: 16 } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>

          {/* HEADER */}
          <Box sx={{ p: 3, borderBottom: "1px solid #E2E8F0" }}>
            <Typography variant="h6" fontWeight={600}>
              {bulkDrawer.type === "price" && (
                bulkDrawer.ratePlanId
                  ? `Prix — ${drawerPlan?.name}`
                  : "Configurer un prix"
              )}
              {bulkDrawer.type === "inventory" && "Modifier le stock"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Appliquer sur tout le mois affiché
            </Typography>
          </Box>

          {/* CONTENT */}
          <Box sx={{ flex: 1, p: 3, display: "flex", flexDirection: "column", gap: 2 }}>

            {/* Sélection rate plan — seulement si pas déjà défini.
                🆕 ASSOCIATION — on ne propose que les tarifs associés à CETTE chambre. */}
            {bulkDrawer.type === "price" && !bulkDrawer.ratePlanId && (
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "#374151" }}>
                  Choisir un tarif
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {allRatePlans
                    .filter(plan => (plan.roomIds || []).map(id => id?.toString?.() ?? id).includes(roomIdStr))
                    .map(plan => {
                      const u = unitOf(plan.stayType);
                      return (
                    <div
                      key={plan._id}
                      onClick={() => setBulkDrawer(prev => ({ ...prev, ratePlanId: plan._id.toString() }))}
                      style={{
                        padding: "10px 14px",
                        borderRadius: 10,
                        border: bulkDrawer.ratePlanId === plan._id.toString()
                          ? "2px solid var(--k-green)"
                          : "1px solid #E2E8F0",
                        backgroundColor: bulkDrawer.ratePlanId === plan._id.toString()
                          ? "var(--k-green-soft)"
                          : "white",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <span>{plan.name}</span>
                      {/* 🆕 PÉRIODES — l'unité dès la sélection, pas après */}
                      <span style={{ fontSize: 11, color: "#64748B", flexShrink: 0 }}>
                        / {u.label}
                      </span>
                    </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* 🆕 PÉRIODES — avertissement d'unité, au-dessus du champ.
                Le montant attendu n'est pas celui d'une nuit. */}
            {bulkDrawer.type === "price" && drawerIsPeriod && (
              <Box sx={{
                p: 2, borderRadius: 2,
                backgroundColor: "var(--k-green-soft)",
                border: "1px solid rgb(var(--k-green-rgb)/0.25)"
              }}>
                <Typography fontSize={12} sx={{ color: "var(--k-green-hover)", fontWeight: 600, mb: 0.5 }}>
                  Prix pour {drawerUnit.label === "mois" ? "un mois entier" : "une semaine entière"}
                </Typography>
                <Typography fontSize={12} sx={{ color: "#475569" }}>
                  Saisissez le montant total de {drawerUnit.nights} nuits, pas un prix par nuit.
                </Typography>
              </Box>
            )}

            {/* INPUT — la saisie reste en euros */}
            <input
              type="number"
              placeholder={
                bulkDrawer.type === "inventory"
                  ? "Nombre de chambres"
                  // 🆕 PÉRIODES — le placeholder porte l'unité
                  : `Prix (€ / ${drawerUnit.label})`
              }
              value={bulkValue}
              onChange={(e) => setBulkValue(e.target.value)}
              style={{
                padding: "10px 14px",
                borderRadius: "10px",
                border: "1px solid #E2E8F0",
                fontSize: 14,
                outline: "none",
                width: "100%"
              }}
            />

            {/* APERÇU */}
            <Box sx={{ p: 2, borderRadius: 2, backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0" }}>
              <Typography variant="body2" color="text.secondary" mb={0.5}>Aperçu</Typography>
              <Typography fontWeight={600}>
                {bulkDrawer.type === "price" && (
                  bulkDrawer.ratePlanId
                    ? `${drawerPlan?.name} → ${bulkValue || 0} € / ${drawerUnit.label}`
                    : "Sélectionnez un tarif"
                )}
                {bulkDrawer.type === "inventory" && `Stock : ${bulkValue || 0} chambre(s)`}
              </Typography>

              {/* 🆕 DEVISES — équivalent KMF en direct pendant la saisie */}
              {bulkDrawer.type === "price" && bulkDrawer.ratePlanId && bulkValue > 0 && (
                <Typography variant="body2" sx={{ mt: 0.5, color: "#64748B" }}>
                  soit {formatKmf(eurToKmf(bulkValue))} KMF / {drawerUnit.label}
                </Typography>
              )}

              {/* 🆕 PÉRIODES — équivalent par nuit, purement indicatif.
                  Remplace l'ancien « × 30 », qui multipliait un prix déjà
                  mensuel et affichait donc trente fois le montant réel. */}
              {bulkDrawer.type === "price" && drawerIsPeriod && bulkValue > 0 && (
                <Typography variant="body2" sx={{ mt: 0.5, color: "var(--k-green)", fontWeight: 600 }}>
                  ≈ {Math.round(Number(bulkValue) / drawerUnit.nights)} € / nuit pour le client
                </Typography>
              )}
            </Box>

            {/* ✅ INDICATION — tooltip sur les cellules jaunes */}
            {bulkDrawer.type === "price" && (
              <Box sx={{
                p: 2, borderRadius: 2,
                backgroundColor: "#EFF6FF",
                border: "1px solid #BFDBFE"
              }}>
                <Typography fontSize={12} color="#1D4ED8">
                  💡 Vous pouvez aussi cliquer sur une cellule jaune dans le calendrier pour modifier le prix d'une période spécifique.
                </Typography>
              </Box>
            )}

          </Box>

          {/* FOOTER */}
          <Box sx={{ p: 3, borderTop: "1px solid #E2E8F0", display: "flex", gap: 2 }}>
            <Button fullWidth variant="outlined" onClick={() => setBulkDrawer({ open: false })}>
              Annuler
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={!bulkValue || (bulkDrawer.type === "price" && !bulkDrawer.ratePlanId)}
              onClick={handleApplyBulk}
            >
              Appliquer
            </Button>
          </Box>

        </Box>
      </Drawer>

    </Box>
  );
};

export default InventoryGrid;